<script setup>
import { ref, watch, computed } from 'vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import FileUploadWidget from '@/presentation/components/input/FileUploadWidget.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'

const props = defineProps({
  product: { type: Object, default: null }
})
const emit = defineEmits(['close', 'success', 'error'])

const saveTriggerRef = ref(null)
const isLoading = ref(false)

const isEditing = computed(() => !!props.product?.id)

const defaultForm = () => ({
  name: '',
  description: '',
  price: 0,
  target: 'rider',
  category: '',
  stock: 0,
  image: '',
  currency: 'XOF',
})

const form = ref(defaultForm())

watch(() => props.product, (p) => {
  if (p) {
    form.value = {
      name: p.name || '',
      description: p.description || '',
      price: p.price || 0,
      target: p.target || 'rider',
      category: p.category || '',
      stock: p.stock || 0,
      image: p.image || '',
      currency: p.currency || 'XOF',
    }
  } else {
    form.value = defaultForm()
  }
}, { immediate: true })

// Handle FileUploadWidget upload result
const handleImageSaved = (data) => {
  if (data?.files?.length > 0 && data.files[0].url) {
    form.value.image = data.files[0].url
  }
}

const submit = () => {
  if (!form.value.name?.trim()) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le nom du produit est requis', type: 'error' })
    return
  }
  if (!form.value.price || Number(form.value.price) <= 0) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le prix doit être supérieur à 0', type: 'error' })
    return
  }
  if (!form.value.stock || Number(form.value.stock) < 1) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le stock doit être au moins 1', type: 'error' })
    return
  }
  isLoading.value = true
  const payload = isEditing.value
    ? { id: props.product.id, ...form.value }
    : { ...form.value }
  saveTriggerRef.value?.trigger(payload)
}

const onSuccess = () => {
  isLoading.value = false
  emit('success')
}

const onError = (err) => {
  isLoading.value = false
  emit('error', err)
}
</script>

<template>
  <!-- Overlay -->
  <div class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" @click.self="$emit('close')">
    <div class="bg-white w-full max-w-xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 class="text-lg font-bold text-gray-900">{{ isEditing ? 'Modifier le produit' : 'Nouveau produit' }}</h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Form -->
      <div class="px-6 py-5 space-y-5 overflow-y-auto flex-1">

        <!-- Image Upload -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Image du produit</label>
          <FileUploadWidget
            :defaultUrls="form.image ? [form.image] : []"
            :maxFiles="1"
            :acceptedFiles="['image/jpeg', 'image/png', 'image/webp']"
            @saved="handleImageSaved"
          />
        </div>

        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Nom du produit <span class="text-rose-500">*</span></label>
          <input v-model="form.name" type="text" placeholder="Ex: Casque de livraison" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea v-model="form.description" rows="2" placeholder="Description courte du produit" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"></textarea>
        </div>

        <!-- Price + Currency -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Prix <span class="text-rose-500">*</span></label>
            <input v-model.number="form.price" type="number" min="0" placeholder="0" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Devise</label>
            <select v-model="form.currency" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500">
              <option value="XOF">XOF</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>

        <!-- Target + Stock -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Cible</label>
            <select v-model="form.target" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500">
              <option value="restaurant">Restaurant</option>
              <option value="rider">Livreur</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Stock</label>
            <input v-model.number="form.stock" type="number" min="0" placeholder="0" class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
          </div>
        </div>

        <!-- Category -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
          <input v-model="form.category" type="text" placeholder="Ex: Équipement, Accessoire..." class="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
        </div>

      </div>

      <!-- Footer buttons -->
      <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
        <button @click="$emit('close')" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          Annuler
        </button>
        <button @click="submit" :disabled="isLoading" class="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors flex items-center gap-2">
          <span v-if="isLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          {{ isEditing ? 'Enregistrer' : 'Créer le produit' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Triggers -->
  <ActionTrigger
    :dataKey="isEditing ? 'updateProduct' : 'createProduct'"
    ref="saveTriggerRef"
    @success="onSuccess"
    @failed="onError"
    dispatchOnSuccess="PRODUCTS_UPDATED"
  />
</template>

<style scoped>
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter-from, .slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
